import { useState } from 'react';

import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { getNumericValueFromString } from './getNumericValueFromString';
import { TurnoverField } from './TurnoverField';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import {
    TApplicationWithTurnover,
    useCreateNewApplication
} from '../../../multiLoanDashboard/NewOutstandingView/CreateNewApplicationForm/useCreateNewApplication';

type TFormValues = {
    turnover: string;
    duration:
        'few_months' | 'ongoing' | 'one_to_two_years' | 'three_plus_years';
};

export const RepaymentReductionForm = ({
    outstandingPrincipal,
    companyTurnover,
    onSuccess
}: {
    outstandingPrincipal: number;
    companyTurnover: number;
    onSuccess: () => void;
}) => {
    const stateKey = useStateKey();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { createNewApplication } = useCreateNewApplication(stateKey);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);

        try {
            const form = event.target as HTMLFormElement;
            const formData = new FormData(form);
            const data = Object.fromEntries(
                formData.entries()
            ) as unknown as TFormValues;

            if (!form.checkValidity()) {
                const firstInvalid =
                    form.querySelector<HTMLElement>(':invalid');
                if (firstInvalid) {
                    firstInvalid.focus();
                    setIsSubmitting(false);
                    return;
                }
            }

            const application: TApplicationWithTurnover = {
                company_turnover: getNumericValueFromString(data.turnover),
                amount: outstandingPrincipal,
                requested_cash_amount: 0,
                product_type: 'credit_facility',
                duration: {
                    unit: 'months',
                    amount: 60
                },
                qualitative_duration: 'three_plus_years'
            };

            await createNewApplication(application);
            setIsSubmitting(false);
            onSuccess();
        } catch (error) {
            setIsSubmitting(false);
            console.log(error);
        }
    };

    return (
        <form
            data-testid="repayment-reduction-form"
            onSubmit={handleSubmit}
            className="w-full space-y-xl"
            noValidate
        >
            <TurnoverField defaultValue={companyTurnover} />
            <Button type="submit" loading={isSubmitting} className="w-full">
                <FormattedMessage
                    id="_.topup_choice.check_my_eligibility"
                    defaultMessage="Check my eligibility"
                />
            </Button>
        </form>
    );
};
